import { ImageObject, NewsArticle, Thing, WithContext } from 'schema-dts';
import type { AppConstructor } from '@/shared/components/constructor/Constructor';
import type { AppBreadcrumbs } from '@/shared/components/shared/Breadcrumbs';
import type { ImageShape } from '@/shared/lib/types';
import { ddMmYyyyToIso8601Date } from '@/shared/lib/utils/dates';
import { composeSchemaGraph } from '@/shared/lib/utils/page-json-ld';
import { oneLine, schemaEventImage, schemaIdRef } from '@/shared/lib/utils/schema-common';
import { SCHEMA_ORGANIZATION_NAME, getSchemaIds, schemaAbsoluteUrl } from '@/shared/lib/utils/schema-ids';

export type NewsArticleJsonLdInput = {
    title: string;
    description: string;
    categorySlug: string;
    newsSlug: string;
    dateDisplay: string;
    publishedAtIso?: string;
    updatedAtIso?: string;
    canonical?: string;
    content: AppConstructor;
    breadcrumbs?: AppBreadcrumbs | null;
};

function firstImageFromContent(content: AppConstructor): ImageShape | undefined {
    for (const block of content) {
        const p = block.properties as Record<string, unknown>;
        if (block.name === 'image') {
            const image = p.image as ImageShape | undefined;
            if (image?.src) return image;
        }
        if (block.name === 'slider') {
            const images = p.images as ImageShape[] | undefined;
            if (images?.[0]?.src) return images[0];
        }
        if (block.name === 'quote') {
            const quote = p.quote as { image?: ImageShape } | undefined;
            if (quote?.image?.src) return quote.image;
        }
    }
    return undefined;
}

function resolveArticleUrl(input: NewsArticleJsonLdInput): string {
    const c = input.canonical?.trim();
    if (c && (c.startsWith('http://') || c.startsWith('https://'))) return c;
    return schemaAbsoluteUrl(`/news/${input.categorySlug}/${input.newsSlug}/`);
}

export function generateNewsArticleJsonLd(input: NewsArticleJsonLdInput): WithContext<Thing>[] {
    const ids = getSchemaIds();
    const articleUrl = resolveArticleUrl(input);
    const datePublished =
        input.publishedAtIso || (input.dateDisplay ? ddMmYyyyToIso8601Date(input.dateDisplay) : undefined) || undefined;
    const dateModified = input.updatedAtIso || input.publishedAtIso || datePublished;
    const firstImage = firstImageFromContent(input.content);
    const imageUrl = schemaEventImage(firstImage?.src);
    const descriptionText = input.description?.trim() ? oneLine(input.description) : '';

    const image: ImageObject | undefined = imageUrl
        ? {
              '@type': 'ImageObject',
              url: imageUrl,
              ...(firstImage?.width != null && { width: String(firstImage.width) }),
              ...(firstImage?.height != null && { height: String(firstImage.height) }),
          }
        : undefined;

    const article: WithContext<NewsArticle> = {
        '@context': 'https://schema.org',
        '@type': 'NewsArticle',
        headline: input.title,
        ...(descriptionText ? { description: descriptionText } : {}),
        ...(datePublished ? { datePublished } : {}),
        ...(dateModified ? { dateModified } : {}),
        url: articleUrl,
        ...(image ? { image } : {}),
        author: schemaIdRef(ids.organization) as NewsArticle['author'],
        publisher: {
            '@type': 'Organization',
            '@id': ids.organization,
            name: SCHEMA_ORGANIZATION_NAME,
            logo: {
                '@type': 'ImageObject',
                url: schemaAbsoluteUrl('/img/logo.svg'),
            },
        },
        mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `${articleUrl}#webpage`,
            url: articleUrl,
        },
    };

    return composeSchemaGraph({
        nodes: [article as WithContext<Thing>],
        breadcrumbs: input.breadcrumbs,
    });
}
